/**
 * THE AGREEMENT'S SHAPES: what the firm's administrator reads about the
 * firm's move, and what they send when they agree (spec §4.8, §5.3; research
 * 01 §2.3.4 to §2.4; research 09 §8.2, §8.3).
 *
 * BROWSER-SAFE: `effect` only. The view group's contract references these
 * schemas and the settings slice imports the contract by path.
 *
 * THE RESOLUTION ON THE WIRE IS LOOSE ON PURPOSE. The resolution's real schema
 * is the matcher's (`Resolution` in the matcher leaf), and leaves import each
 * other through `index.ts` only, which publishes a repository that reaches
 * `@effect/sql-pg`; a browser-safe contract cannot name it. So the payload
 * carries the resolution's structure with its closed words as strings, and the
 * service decodes it through the matcher's own `Resolution` before anything
 * else, so a word the matcher does not know is a 400 naming the pair. The
 * slice builds the value from the matcher's domain module (outsiders may), so
 * the browser is typed against the real schema all the same.
 *
 * NO NAME, NUMBER OR ADDRESS IS STORED OR RETURNED HERE. The resolution holds
 * ids, closed words and which fields agreed, never a value the firm typed.
 */
import { Schema as S } from "effect"

import { WrittenInstruction } from "../../../shared/domain/written-instruction"

/** The two scopes (decision 001). No per-table picker (spec §11). */
export const AGREEMENT_SCOPES = ["recommended", "everything"] as const
export const AgreementScope = S.Literal(...AGREEMENT_SCOPES).annotations({ identifier: "CutoverAgreementScope" })
export type AgreementScope = typeof AgreementScope.Type

/** `insurance_broker_bms.org_migrations.state` (v5.09/010). */
export const MIGRATION_STATES = ["legacy", "migrating", "migrated", "failed"] as const
export const MigrationState = S.Literal(...MIGRATION_STATES).annotations({ identifier: "CutoverMigrationState" })
export type MigrationState = typeof MigrationState.Type

/**
 * THE LOUD BOUND on the pairs one agreement carries (gate 6 of the reads and
 * writes spec: every payload array is capped). One pair per legacy client of
 * the firm; dev's largest firm has 191 (research 09 §4.3) and production rates
 * are UNMEASURED (research 09 U-12), so the cap is two orders above the one
 * firm measured. The pairs land as one JSONB value in one row, so the cap
 * guards the request's size, not a bind count.
 */
export const AGREEMENT_PAIRS_CAP = 20_000

/** The matcher's evidence list is eight words; a pair never carries more. */
export const AGREEMENT_EVIDENCE_CAP = 16

export const AgreementPairPayload = S.Struct({
  legacyId: S.UUID,
  bookId: S.NullOr(S.UUID),
  bucket: S.String,
  decision: S.String,
  evidence: S.Array(S.String).pipe(S.maxItems(AGREEMENT_EVIDENCE_CAP)),
  /**
   * `together` only: the keeper, as `ResolutionPair` carries it. Declared here
   * so the wire never strips it (an undeclared key is dropped on encode, and
   * the server would read every together pair as keeperInvalid).
   */
  together: S.optional(S.Struct({ legacyId: S.UUID })),
  /** `archive` only: the Cova CRM client it duplicates, recorded only; null or absent when none. */
  duplicateOf: S.optional(S.NullOr(S.UUID)),
}).annotations({ identifier: "CutoverAgreementPairPayload" })

export const ResolutionPayload = S.Struct({
  version: S.Literal(1),
  pairs: S.Array(AgreementPairPayload).pipe(S.maxItems(AGREEMENT_PAIRS_CAP)),
}).annotations({ identifier: "CutoverResolutionPayload" })
export type ResolutionPayload = typeof ResolutionPayload.Type

/** What the administrator sends when they agree. */
export const AgreePayload = S.Struct({
  /** The text version they read; must be the current one. */
  agreementVersion: S.String,
  scope: AgreementScope,
  resolution: ResolutionPayload,
}).annotations({ identifier: "CutoverAgreePayload" })
export type AgreePayload = typeof AgreePayload.Type

/**
 * THE FIRM'S WRITTEN INSTRUCTION behind an agreement Cova staff recorded for it
 * (Tai, 2026-11-03): whose instruction and a reference to it. Firm-facing:
 * it never names the staff member, who is on the staff trail only.
 */
export const OnBehalf = S.Struct({
  instructedBy: S.String,
  reference: S.String,
}).annotations({ identifier: "CutoverAgreementOnBehalf" })
export type OnBehalf = typeof OnBehalf.Type

/** What the firm reads for an agreement Cova staff recorded on its instruction. */
export const onBehalfLine = (onBehalf: OnBehalf): string =>
  `Cova staff recorded this on ${onBehalf.instructedBy}'s written instruction (${onBehalf.reference}).`

/** What Cova staff send to agree on a firm's behalf: the administrator's payload and the firm's written instruction, flat. */
export const AgreeOnBehalfPayload = S.Struct({
  ...AgreePayload.fields,
  ...WrittenInstruction.fields,
}).annotations({ identifier: "CutoverAgreeOnBehalfPayload" })
export type AgreeOnBehalfPayload = typeof AgreeOnBehalfPayload.Type

/** The firm's latest agreement, as counts. The stored resolution itself never leaves the server. */
export const AgreementSummary = S.Struct({
  id: S.UUID,
  /** ISO 8601. */
  agreedAt: S.String,
  agreementVersion: S.String,
  scope: AgreementScope,
  /** Legacy clients resolved onto a book client (`same` or `handPicked`). */
  matched: S.Int,
  /** Legacy clients that copy across as new book clients (`different` or `new`). */
  copiedAsNew: S.Int,
  /** Legacy clients that arrive archived. Optional so an older answer decodes. */
  archived: S.optional(S.Int),
  /** Legacy clients kept together with another of the firm's legacy clients. Optional so an older answer decodes. */
  keptTogether: S.optional(S.Int),
  /** Whether the caller is the administrator who agreed. */
  agreedByCaller: S.Boolean,
  /**
   * Set when Cova staff recorded this agreement on the firm's written
   * instruction; then no administrator agreed to it, and the page says so.
   * Optional on the wire so an older answer still decodes.
   */
  onBehalf: S.optional(S.NullOr(OnBehalf)),
}).annotations({ identifier: "CutoverAgreementSummary" })
export type AgreementSummary = typeof AgreementSummary.Type

/**
 * WHETHER THE FIRM'S FIRST READ STILL HOLDS ITS AGREEMENT BACK (decision 036,
 * follow-up 3). `required` while the firm's sync is on (paths 2 and 3);
 * `complete` once a connection has finished a scan of every client and
 * contact completeness stream, and always for a firm with its sync off. The
 * page draws the read's progress from the sync's own status view; this is the
 * server's answer, which the agreement and the move both enforce.
 */
export const AgreementReadGate = S.Struct({
  required: S.Boolean,
  complete: S.Boolean,
}).annotations({ identifier: "CutoverAgreementReadGate" })
export type AgreementReadGate = typeof AgreementReadGate.Type

/**
 * What an agreement or a move refused for the read says (decision 036,
 * follow-up 3). Here and not in the sync leaf, whose engine side never names
 * the provider; the page draws the read's progress beside it.
 */
export const READ_PENDING_REFUSAL =
  "Steadfast Apps is still reading your clients and contacts. You can agree and move once that first read is done."

/**
 * Where a firm sets its classes up before it moves: the Classes pane of the
 * Sync page. A firm waiting to come onto Cova CRM keeps only that page in
 * Settings (decision 036), so the register's Classes page is no answer.
 */
export const CLASSES_PENDING_PLACE = "Settings, Steadfast Apps Sync, Classes"

/**
 * What the move refuses, and the agreement page says, while the firm's classes
 * are not set up (decision 032 S24), one line for both: it names the place by
 * its titles and says the move waits, never the agreement. Beside the read's
 * words, which the move borrows the same way.
 */
export const CLASSES_PENDING_REFUSAL = `The move waits until the firm's classes are set up: ${CLASSES_PENDING_PLACE}.`

/**
 * Where the firm's move stands, for the page and the root banner.
 *
 * - `open`: an agreement can be written now (031's rule: no state row or
 *   `legacy` with no agreement yet, or `failed`).
 * - `awaitingAgreement`: support has asked and the firm owes an answer: still
 *   `legacy` with no current agreement, or `failed` and asked again since the
 *   agreement the run used. The banner shows on this and nothing else.
 */
export const AgreementStatus = S.Struct({
  organizationId: S.UUID,
  state: S.NullOr(MigrationState),
  /** ISO 8601, when support requested the move; null when nobody has. */
  requestedAt: S.NullOr(S.String),
  agreement: S.NullOr(AgreementSummary),
  open: S.Boolean,
  awaitingAgreement: S.Boolean,
  /** The text version an agreement written now records. */
  currentVersion: S.String,
  /** Whether the first read still holds the agreement back (decision 036). */
  readGate: AgreementReadGate,
  /** Decision 032 S24: whether the firm's classes are set up, which the move needs; true with no connection. */
  classesSetUp: S.Boolean,
}).annotations({ identifier: "CutoverAgreementStatus" })
export type AgreementStatus = typeof AgreementStatus.Type

/**
 * 031's insert rule (as 041 restates it), read in the application so the page
 * can say it before the database refuses. `hasAgreement` is a CURRENT
 * agreement: a withdrawn one does not count.
 */
export const agreementOpen = (state: MigrationState | null, hasAgreement: boolean): boolean =>
  state === "failed" || ((state === null || state === "legacy") && !hasAgreement)

/**
 * The banner's rule: support has asked and the firm owes an answer.
 *
 * - `legacy`: asked, and no current agreement (none yet, or the latest was
 *   withdrawn by staff, which reads as none).
 * - `failed`: asked AFTER the agreement the failed run used, so a re-ask after
 *   a failed move brings the banner back as well as the email. An ask older
 *   than the agreement is the one that agreement already answered.
 *
 * `agreedAt` is the firm's current agreement's instant, or null when it has none.
 */
export const awaitingAgreement = (
  state: MigrationState | null,
  requestedAt: string | null,
  agreedAt: string | null,
): boolean => {
  if (requestedAt === null) return false
  if (state === "legacy") return agreedAt === null
  if (state === "failed") return agreedAt === null || Date.parse(agreedAt) < Date.parse(requestedAt)
  return false
}

/** How a resolution's pairs divide, the one count the agreement's event, its summary and the page all use. */
export const countsOf = (
  pairs: ReadonlyArray<{ readonly decision: string }>,
): { readonly matched: number; readonly copiedAsNew: number; readonly archived: number; readonly keptTogether: number } => {
  let matched = 0
  let copiedAsNew = 0
  let archived = 0
  let keptTogether = 0
  for (const pair of pairs) {
    if (pair.decision === "same" || pair.decision === "handPicked") matched += 1
    else if (pair.decision === "different" || pair.decision === "new") copiedAsNew += 1
    else if (pair.decision === "archive") archived += 1
    else if (pair.decision === "together") keptTogether += 1
  }
  return { matched, copiedAsNew, archived, keptTogether }
}
